import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { Keys } from '../../../ts/helpers/Keys';
import { imgUrl } from '../../utilities/ImgUrl';
import { CommonStyles } from '../CommonStyles';
import { escapeRegexp, FormattedMatch } from '../FormattedMatch';
import { Location } from '../Location';
import { SearchBox } from './SearchBox';

export class FilteringListBox<T> extends React.Component<FilteringListBox.Props<T>, FilteringListBox.State<T>> {

  static displayName = 'FilteringListBox';

  static sectionSize = 10;

  static ItemRefPrefix = 'item';

  public static element = React.createFactory(FilteringListBox);

  // React routines

  state: FilteringListBox.State<T> = {
    data: [],
    selectedIndex: -1,
    searchTerm: '',
    searchBoxHeight: 0,
    measuredRowHeight: 0,
    indexOfFirstVisibleSection: 0,
    indexOfLastVisibleSection: 0,
    anyIcons: false,
    maxCountDigits: 0
  };

  public render() {
    // render sections
    let sectionsWrapper: JSX.Element = null;
    if (this.state.searchTerm && (!this.state.data || this.state.data.length <= 0)) {
      sectionsWrapper = React.createElement('div', {
        'data-tb-test-id':'tb-no-results',
        style: { textAlign: 'center' } // TODO move some px down
      } as React.HTMLAttributes<HTMLDivElement>,
      Messages.noMatchingResults());
    } else {
      sectionsWrapper = React.createElement('div', {
        style: {
          height: this.state.data.length * this.state.measuredRowHeight,
          overflow: 'hidden'
        }
      }, this.renderSections());
    }

    const listBoxBaseStyle = {
      position: 'absolute',
      left: 0,
      right: 0,
      top: this.state.searchBoxHeight + CommonStyles.Sizes.SmallSpace,
      bottom: 0,
      overflowX: 'hidden',
      overflowY: 'auto'
    };

    const listBoxStyle = this.props.listBoxStyle ? _.extend(listBoxBaseStyle, this.props.listBoxStyle) : listBoxBaseStyle;

    return React.createElement('div',
      {
        style: this.props.style
      },
      SearchBox.element({
        ref: 'searchBox',
        inputProps: {
          placeholder: this.props.searchBoxPlaceholder,
          onChange: (event: React.UIEvent<HTMLInputElement>) => this.searchTermChanged(event),
          onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => this.handleKeyDown(event),
          style: { width: '100%' }
        }
      }),
      React.createElement('div', {
        'data-tb-test-id':'tb-filtering-list-box',
        ref: 'listbox',
        style: listBoxStyle,
        onScroll: (event: React.UIEvent<HTMLDivElement>) => this.onScroll(event)
      } as React.HTMLAttributes<HTMLDivElement>,
      sectionsWrapper));
  }

  private renderSections() {
    const matchPrefixesOnly = this.matchPrefixesOnly();
    const sectionHeight = FilteringListBox.sectionSize * this.state.measuredRowHeight;

    const sections: React.ReactElement<any>[] = [];

    for (let i = this.state.indexOfFirstVisibleSection; i <= this.state.indexOfLastVisibleSection; i++) {

      const firstRowIndex = i * FilteringListBox.sectionSize;
      const lastRowIndex = Math.min(firstRowIndex + FilteringListBox.sectionSize - 1, this.state.data.length - 1);

      const sectionRows: React.ReactElement<any>[] = [];
      for (let rowIndex = firstRowIndex; rowIndex <= lastRowIndex; rowIndex++) {
        const item = this.state.data[rowIndex];
        sectionRows.push(
          FilteringListBox.Row.element({
            key: rowIndex,
            ref: FilteringListBox.ItemRefPrefix + rowIndex,
            item: item,
            searchTerm: this.state.searchTerm,
            matchPrefixesOnly: matchPrefixesOnly,
            selected: rowIndex === this.state.selectedIndex,
            showIcon: this.state.anyIcons,
            maxCountDigits: this.state.maxCountDigits,
            onClick: () => {
              if (this.props.itemSelected) {
                this.props.itemSelected(item);
              }
            }
          }));
      }

      const marginTop = i === this.state.indexOfFirstVisibleSection ? i * sectionHeight : 0;

      sections.push(React.createElement('div', {
          key: i,
          'data-tb-test-id': 'tb-filtering-list-box-items',
          style: {
            marginTop: marginTop,
            height: sectionHeight,
            overflow: 'hidden'
          }
        } as React.HTMLAttributes<HTMLDivElement>,
        sectionRows));
    }

    return sections;
  }

  private matchPrefixesOnly(): boolean {
    return _.isBoolean(this.props.matchPrefixesOnly) ? this.props.matchPrefixesOnly : true;
  }

  private searchTermChanged(event: React.UIEvent<HTMLInputElement>) {
    const searchTerm = (event.currentTarget).value.toLowerCase();
    // For a non-empty search term, automatically select the top-most matching item
    const selectedIndex = searchTerm.length === 0 ? -1 : 0;

    this.resetScrollPosition();

    this.setState({
      searchTerm: searchTerm,
      selectedIndex: selectedIndex
    }, () => this.fetch());
  }

  private resetScrollPosition() {
    const viewPortNode = $(ReactDOM.findDOMNode(this.refs['listbox']));
    viewPortNode.scrollTop(0);
  }

  private handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    switch(event.keyCode) {
      case Keys.ArrowUp:
        this.setState({
          selectedIndex: Math.max(this.state.selectedIndex - 1, 0)
        }, () => this.scrollItemIntoView());
        event.preventDefault();
        event.stopPropagation();
        break;

      case Keys.ArrowDown:
        this.setState({
            selectedIndex: Math.min(this.state.selectedIndex + 1, this.state.data.length - 1)
          }, () => this.scrollItemIntoView());
        event.preventDefault();
        event.stopPropagation();
        break;

      case Keys.Enter:
        if (this.state.selectedIndex >= 0 && this.state.selectedIndex < this.state.data.length) {
          const selectedItem = this.state.data[this.state.selectedIndex];

          // Navigate to the selected item's url, if any.
          if (selectedItem.url) {

            // TODO: I'd prefer to use getDefaultProps to provide a default location service, but
            // it doesn't seem to work with TypeScript's class syntax.
            const locationService = this.props.locationService || Location.instance;

            locationService.setUrl(selectedItem.url);
          }

          if (this.props.itemSelected) {
            this.props.itemSelected(selectedItem);
          }
          event.preventDefault();
          event.stopPropagation();
        }
        break;
    }
  }

  private scrollItemIntoView() {

    const viewPortNode = $(ReactDOM.findDOMNode(this.refs['listbox']));

    const scrollTop = viewPortNode.scrollTop();
    const viewPortBottom = viewPortNode.height();

    const itemTop = this.state.selectedIndex * this.state.measuredRowHeight;
    const itemBottom = itemTop + this.state.measuredRowHeight;

    if (itemTop < scrollTop) {
      viewPortNode.scrollTop(itemTop);
    } else if (itemBottom > scrollTop + viewPortBottom) {
      viewPortNode.scrollTop(itemBottom - viewPortBottom);
    }
    this.updateVisibleSectionIndexes();
  }

  public componentDidMount() {
    // Initially focus on the search box
    const searchBox = $(ReactDOM.findDOMNode(this.refs['searchBox']));
    setTimeout(() => searchBox.focus(), 0);

    // Record the height of the search box
    this.setState({ searchBoxHeight: searchBox.outerHeight() });

    // Load the initial batch of data
    this.fetch();
  }

  private updateMaxCountDigitsAndIcons() {
    const anyIcons = this.state.anyIcons || _.any(this.state.data, (item: FilteringListBox.Item<T>) => item && _.isString(item.icon));

    // Determine the maximum number of digits in the item counts. This is used to calculate
    // the width of the 'count' column.
    const maxCount = _.max(_.map(this.state.data, (item: FilteringListBox.Item<T>) => (item && item.count || 0)));
    const maxCountDigits = ('' + maxCount).length;
    this.setState({
      anyIcons: anyIcons,
      maxCountDigits: maxCountDigits
    });
  }

  private measureRowHeight() {
    // Measure the height of the first row we find
    const firstRowKey = _.find(_.keys(this.refs),
      (key: string) => key.substr(0, FilteringListBox.ItemRefPrefix.length) === FilteringListBox.ItemRefPrefix);
    const firstRowNode = $(ReactDOM.findDOMNode(this.refs[firstRowKey]));
    if (firstRowNode.length > 0) {
      this.setState({measuredRowHeight: firstRowNode.outerHeight()});
    }
  }

  private onScroll(event: React.UIEvent<HTMLDivElement>) {
    this.updateVisibleSectionIndexes();
  }

  private updateVisibleSectionIndexes() {

    const visibleSections = this.calculateVisibleSectionIndexes();

    if (visibleSections.indexOfFirstVisibleSection !== this.state.indexOfFirstVisibleSection
        || visibleSections.indexOfLastVisibleSection !== this.state.indexOfLastVisibleSection) {
      this.setState(visibleSections, () => this.fetch());
    }
  }

  private calculateVisibleSectionIndexes() {
    const measuredRowHeight = this.state.measuredRowHeight || 100;
    const sectionHeight = FilteringListBox.sectionSize * measuredRowHeight;

    const viewPortNode = $(ReactDOM.findDOMNode(this.refs['listbox']));
    const viewportTop = viewPortNode.scrollTop();
    const viewportBottom = viewportTop + viewPortNode.height();

    const indexOfFirstVisibleSection = Math.floor(viewportTop / sectionHeight);
    const indexOfLastVisibleSection = Math.ceil(viewportBottom / sectionHeight);

    return {
      indexOfFirstVisibleSection: indexOfFirstVisibleSection,
      indexOfLastVisibleSection: indexOfLastVisibleSection
    };
  }

  private fetch() {
    const startIndex = this.state.indexOfFirstVisibleSection * FilteringListBox.sectionSize;
    const endIndex = (this.state.indexOfLastVisibleSection + 1) * FilteringListBox.sectionSize - 1;

    this.props.itemStore.getItems(this.state.searchTerm, startIndex, endIndex).then((data: FilteringListBox.Item<T>[]) => {
      this.setState({ data: data }, () => {
        this.updateMaxCountDigitsAndIcons();
        if (this.state.measuredRowHeight === 0) {
          setTimeout(() => this.measureRowHeight(), 0);
        }
      });
    });
  }

}

export module FilteringListBox {

  export interface Props<T> {
    itemStore: ItemStore<Item<T>>;
    searchBoxPlaceholder?: string;
    matchPrefixesOnly?: boolean;
    style?: any;
    listBoxStyle?: any;
    itemSelected?: (item: Item<T>) => void;

    // Dependencies
    locationService?: Location;
  }

  export interface State<T> {
    data?: Item<T>[];
    selectedIndex?: number;
    searchTerm?: string;
    searchBoxHeight?: number;
    measuredRowHeight?: number;
    indexOfFirstVisibleSection?: number;
    indexOfLastVisibleSection?: number;
    anyIcons?: boolean;
    maxCountDigits?: number;
  }

  export interface Item<T> {
    label: string;
    url?: string;
    icon?: string;
    count?: number;
    isDivider?: boolean;
    iconTitle?: string;
    rawItem: T;
  }

  export interface ItemStore<T> {
    getItems(filter: string, start: number, end: number): Promise<T[]>;
  }


  export class Row<T> extends React.Component<Row.Props<T>, Row.State> {

    static displayName = 'FilteringListBoxRow';

    public static element = React.createFactory(Row);

    state: Row.State = {
      hovered: false
    };

    private handleMouseOver() {
      this.setState({ hovered: true });
    }

    private handleMouseOut() {
      this.setState({ hovered: false });
    }

    public render() {

      const item = this.props.item;
      const hasCount = item && _.isNumber(item.count);
      const hasIcon = this.props.showIcon && item && item.icon;
      const hasEmptyIcon = this.props.showIcon && (!item || !item.icon);

      const topBottomItemPadding = CommonStyles.Sizes.DefaultSpace + CommonStyles.Sizes.LineWidth;

      let backgroundColor: string = null;
      if (this.state.hovered) {
        backgroundColor = CommonStyles.Colors.Grey245;
      }
      if (this.props.selected) {
        backgroundColor = CommonStyles.Colors.BlueHighlight;
      }

      const rowStyle: any = {
        display: 'flex',
        paddingLeft: hasEmptyIcon ? CommonStyles.Sizes.SmallIconSize : 0,
        paddingRight: 0,
        paddingTop: topBottomItemPadding,
        paddingBottom: topBottomItemPadding,
        backgroundColor: backgroundColor,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        color: CommonStyles.Colors.NormalTextColor
      };

      // If the item hasn't been fetched yet, display an empty row
      if (!item) {
        return React.createElement('span', {
          style: rowStyle
        });
      }

      if (item && item.isDivider) {
        rowStyle.paddingBottom--;
        rowStyle.borderBottom = '1px solid ' + CommonStyles.Colors.Grey225;
      }

      // Max digit width is about 7px, so allocate '7 * number of digits' width.
      const countWidth = 7 * this.props.maxCountDigits;

      const itemCount = hasCount
        ? React.createElement('span', {
            style: {
              marginLeft: 'auto',
              width: countWidth,
              paddingLeft: CommonStyles.Sizes.DefaultSpace,
              paddingRight: CommonStyles.Sizes.SmallSpace
            }
          }, item.count)
        : null;

      const itemLabel = this.itemLabel(hasCount, countWidth);

      const itemIcon = hasIcon ? this.itemIcon() : null;

      return React.createElement('a', {
        href: item && item.url,
        style: rowStyle,
        onClick: () => { this.props.onClick(); },
        onMouseOver: () => this.handleMouseOver(),
        onMouseOut: () => this.handleMouseOut(),
        title: item && item.label
      }, itemIcon, itemLabel, itemCount);
    }

    private itemLabel(hasCount: boolean, countWidth: number): React.ReactElement<any> {
      const item = this.props.item;

      const outerCountWidth = countWidth + CommonStyles.Sizes.DefaultSpace + CommonStyles.Sizes.SmallSpace;

      //Width corrections needed to compensate for extra padding that was added to this and the icon element
      const itemIconPaddingCorrection = item && item.icon ? CommonStyles.Sizes.SmallIconSize : 0;
      const itemCountPaddingCorrection = hasCount ? outerCountWidth : 0;
      const widthCorrection = itemIconPaddingCorrection + itemCountPaddingCorrection + CommonStyles.Sizes.DefaultSpace;

      const labelStyle = {
        paddingLeft: CommonStyles.Sizes.DefaultSpace,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        maxWidth: `calc(100% - ${widthCorrection}px)`
      };

      return React.createElement(FormattedMatch, {
        text: item.label,
        searchTerm: this.props.searchTerm,
        matchPrefixesOnly: this.props.matchPrefixesOnly,
        style: labelStyle
      });
    }

    private itemIcon(): React.ReactElement<any> {

      const item = this.props.item;

      const topBottomItemPadding = CommonStyles.Sizes.DefaultSpace + CommonStyles.Sizes.LineWidth;

      const iconStyle: any = {
        backgroundImage: imgUrl(item.icon),
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: CommonStyles.Sizes.SmallIconBackgroundSize,
        marginLeft: 1,
        paddingLeft: CommonStyles.Sizes.SmallIconSize,
        paddingRight: 0,
        paddingTop: topBottomItemPadding,
        paddingBottom: topBottomItemPadding
      };

      return React.createElement('span', {
        style: iconStyle,
        title: item.iconTitle
      });

    }

  }

  export module Row {

    export interface Props<T> {
      item: Item<T>;
      selected: boolean;
      showIcon: boolean;
      searchTerm?: string;
      matchPrefixesOnly: boolean;
      maxCountDigits: number;
      onClick?: () => void;
    }

    export interface State {
      hovered?: boolean;
    }

  }


  /*
   * FilteringListBoxFilter
   *
   * A helper class for doing the data filtering. It's in a separate class to ease testability.
   *
   */
  export interface FilteredData<T> {
    fetchedRows: Item<T>[];
  }

  export module Filter {

    export function filterDataBySearchTerm<T>(data: Item<T>[],
                                              searchTerm: string,
                                              matchPrefixesOnly: boolean): FilteredData<T> {

      const fetchedData = _.compact(data); // Compact to include only the rows that were really fetched

      // If search term is blank, no filtering is necessary. Just return all the data.
      if (searchTerm.length === 0) {
        return {
          fetchedRows: fetchedData
        };
      }

      const regex = regexForSearchTerm(searchTerm, matchPrefixesOnly);
      const filteredData = _.filter(fetchedData, (item: Item<T>) => regex.test(item.label));

      return {
        fetchedRows: filteredData,
      };

    }

    // Constructs a regex for finding items that contain all of the words in the searchTerm.
    // See http://stackoverflow.com/a/5421978 for the derivation of the regex.
    function regexForSearchTerm(searchTerm: string, matchPrefixesOnly: boolean) {

      const searchTermTokens = _.compact(escapeRegexp(searchTerm).split(' '));
      const regexParts = ['^'];
      _.each(searchTermTokens, (token: string) => {
        // (?=) is a lookahead.  Each search term is attempted to be found somewhere
        // (?:^||\\s) attempts to match a word boundary, either matching the string start
        //            or after a space character.  This is preferable to \\b for unicode support
        // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#special-word-boundary
        regexParts.push('(?=.*' + (matchPrefixesOnly ? '(?:^|\\s)' : '') + token + ')');
      });
      return new RegExp(regexParts.join(''), 'gi');
    }

  }

}
